/**
 * ApptrovePricingPage — mobile MMP pricing tiers + quote request + list.
 *
 * UI: four capacity-band tier cards (Indie / Growth / Scale / Enterprise),
 * an inline quote-request form, and a tenant-scoped list of existing quotes
 * with status transition controls. Green-only accent, no dark, no emoji.
 */
import { useState, type FormEvent } from 'react';
import {
	useApptrovePricingList,
	useApptrovePricingCreate,
	useApptrovePricingStatus,
} from './hooks';
import type { AddonFlags, Platform, ScaleBand } from './api';

const CARD =
	'rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm';
const TIER_CARD =
	'flex flex-col gap-3 rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm';
const INPUT =
	'w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20';
const BTN =
	'rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60';
const BTN_GHOST =
	'rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50';
const CHIP =
	'inline-flex items-center gap-1 rounded-[4px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700';
const LABEL = 'text-xs font-semibold uppercase tracking-wider text-zinc-600';

interface TierCopy {
	band: ScaleBand;
	title: string;
	range: string;
	blurb: string;
	features: string[];
	cta: string;
}

const TIERS: readonly TierCopy[] = [
	{
		band: 'indie',
		title: 'Indie',
		range: '< 50k installs / mo',
		blurb: 'For solo devs shipping their first app with real attribution.',
		features: [
			'Core install + event attribution',
			'Self-serve dashboard',
			'Standard SDK, Android + iOS',
		],
		cta: 'Request quote',
	},
	{
		band: 'growth',
		title: 'Growth',
		range: '50k - 500k installs / mo',
		blurb: 'For teams scaling their first paid channels.',
		features: [
			'Everything in Indie',
			'SKAN conversion value modelling',
			'Server-to-server events',
			'Audience builder',
		],
		cta: 'Request quote',
	},
	{
		band: 'scale',
		title: 'Scale',
		range: '500k - 5M installs / mo',
		blurb: 'For growth teams running multi-channel paid at scale.',
		features: [
			'Everything in Growth',
			'pLTV modelling',
			'Incrementality experiments',
			'CTV attribution',
			'Dedicated CSM',
		],
		cta: 'Request quote',
	},
	{
		band: 'enterprise',
		title: 'Enterprise',
		range: '5M+ installs / mo',
		blurb: 'For household-name brands with complex measurement needs.',
		features: [
			'Everything in Scale',
			'Custom data residency',
			'Enterprise SSO + audit log',
			'24/7 tier-blind support',
			'Named solutions architect',
		],
		cta: 'Contact sales',
	},
];

interface FormState {
	est_installs_monthly: string;
	est_events_monthly: string;
	est_mau: string;
	est_mtu: string;
	platforms: Record<Platform, boolean>;
	addons: AddonFlags;
	contact_email: string;
	notes: string;
}

const EMPTY_FORM: FormState = {
	est_installs_monthly: '',
	est_events_monthly: '',
	est_mau: '',
	est_mtu: '',
	platforms: { ios: false, android: false, web: false, ctv: false },
	addons: {},
	contact_email: '',
	notes: '',
};

function toInt(s: string): number {
	const n = Number(s);
	return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : 0;
}

function formatMoney(minor: number | null, currency: string | null): string {
	if (minor === null || currency === null) return '-';
	return `${(minor / 100).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})} ${currency}`;
}

function formatDate(sec: number): string {
	if (!Number.isFinite(sec) || sec <= 0) return '-';
	return new Date(sec * 1000).toISOString().slice(0, 10);
}

function statusPillClass(s: string): string {
	// Green-only palette; zinc for neutral states.
	switch (s) {
		case 'accepted':
			return 'border-green-600 bg-green-50 text-green-800';
		case 'quoted':
			return 'border-green-300 bg-green-50 text-green-700';
		case 'declined':
			return 'border-zinc-300 bg-zinc-50 text-zinc-600';
		default:
			return 'border-zinc-200 bg-white text-zinc-700';
	}
}

export function ApptrovePricingPage() {
	const listQuery = useApptrovePricingList();
	const createMut = useApptrovePricingCreate();
	const statusMut = useApptrovePricingStatus();

	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [selectedTier, setSelectedTier] = useState<ScaleBand | null>(null);

	const handlePlatformToggle = (p: Platform) => {
		setForm((prev) => ({
			...prev,
			platforms: { ...prev.platforms, [p]: !prev.platforms[p] },
		}));
	};

	const handleAddonToggle = (k: keyof AddonFlags) => {
		setForm((prev) => ({
			...prev,
			addons: { ...prev.addons, [k]: !prev.addons[k] },
		}));
	};

	const handleSelectTier = (band: ScaleBand) => {
		setSelectedTier(band);
		// Pre-fill sensible defaults for the chosen band.
		const bandDefaults: Record<ScaleBand, Partial<FormState>> = {
			indie: {
				est_installs_monthly: '10000',
				est_events_monthly: '100000',
				est_mau: '5000',
				est_mtu: '4000',
			},
			growth: {
				est_installs_monthly: '200000',
				est_events_monthly: '2000000',
				est_mau: '100000',
				est_mtu: '80000',
			},
			scale: {
				est_installs_monthly: '2000000',
				est_events_monthly: '20000000',
				est_mau: '1000000',
				est_mtu: '800000',
			},
			enterprise: {
				est_installs_monthly: '10000000',
				est_events_monthly: '100000000',
				est_mau: '5000000',
				est_mtu: '4000000',
			},
		};
		setForm((prev) => ({ ...prev, ...bandDefaults[band] }));
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const platforms = (Object.entries(form.platforms) as [Platform, boolean][])
			.filter(([, on]) => on)
			.map(([p]) => p);
		if (platforms.length === 0) return;
		if (!form.contact_email.trim()) return;
		createMut.mutate(
			{
				est_installs_monthly: toInt(form.est_installs_monthly),
				est_events_monthly: toInt(form.est_events_monthly),
				est_mau: toInt(form.est_mau),
				est_mtu: toInt(form.est_mtu),
				platforms,
				addons: form.addons,
				contact_email: form.contact_email.trim(),
				notes: form.notes.trim() || null,
			},
			{
				onSuccess: () => {
					setForm(EMPTY_FORM);
					setSelectedTier(null);
				},
			},
		);
	};

	const quotes = listQuery.data ?? [];

	return (
		<main
			data-testid="apptrove-pricing-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Mobile MMP Pricing
				</p>
				<h1 className="text-3xl font-semibold text-green-800">
					Apptrove pricing
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Mobile measurement is priced on capacity, not per-seat. Pick the
					scale band that matches your monthly installs to anchor the
					conversation, then request a quote with your exact numbers and the
					attribution vendor add-ons you expect to need.
				</p>
			</header>

			<section
				aria-label="Pricing tiers"
				className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
				data-testid="apptrove-pricing-tiers"
			>
				{TIERS.map((tier) => {
					const selected = selectedTier === tier.band;
					return (
						<article
							key={tier.band}
							className={TIER_CARD}
							aria-selected={selected}
							data-testid={`apptrove-pricing-tier-${tier.band}`}
							data-selected={selected ? 'true' : 'false'}
							style={
								selected
									? { borderColor: 'rgb(22 101 52)', borderWidth: '2px' }
									: undefined
							}
						>
							<header className="flex flex-col gap-1">
								<p className={LABEL}>{tier.title}</p>
								<p className="text-xs text-zinc-500">{tier.range}</p>
							</header>
							<p className="text-sm text-zinc-700">{tier.blurb}</p>
							<ul className="flex flex-col gap-1.5 text-sm text-zinc-700">
								{tier.features.map((f) => (
									<li key={f} className="flex items-start gap-2">
										<span
											aria-hidden="true"
											className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-green-700"
										/>
										<span>{f}</span>
									</li>
								))}
							</ul>
							<p className="text-2xl font-semibold text-green-800">
								Custom
							</p>
							<button
								type="button"
								className={BTN}
								onClick={() => handleSelectTier(tier.band)}
								data-testid={`apptrove-pricing-select-${tier.band}`}
							>
								{tier.cta}
							</button>
						</article>
					);
				})}
			</section>

			<section className={CARD} aria-label="Request a quote">
				<h2 className="text-base font-semibold text-zinc-900">
					Request a quote
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Share your expected monthly capacity and the add-ons you need.
					Attribution vendor pricing is always custom.
				</p>
				<form
					onSubmit={handleSubmit}
					className="mt-4 flex flex-col gap-4"
					data-testid="apptrove-pricing-form"
				>
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Installs / mo</span>
							<input
								type="number"
								min={0}
								className={INPUT}
								value={form.est_installs_monthly}
								onChange={(e) => {
									const v = e.target.value;
									setForm((p) => ({ ...p, est_installs_monthly: v }));
								}}
								data-testid="apptrove-pricing-installs"
							/>
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Events / mo</span>
							<input
								type="number"
								min={0}
								className={INPUT}
								value={form.est_events_monthly}
								onChange={(e) => {
									const v = e.target.value;
									setForm((p) => ({ ...p, est_events_monthly: v }));
								}}
								data-testid="apptrove-pricing-events"
							/>
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>MAU</span>
							<input
								type="number"
								min={0}
								className={INPUT}
								value={form.est_mau}
								onChange={(e) => {
									const v = e.target.value;
									setForm((p) => ({ ...p, est_mau: v }));
								}}
								data-testid="apptrove-pricing-mau"
							/>
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>MTU</span>
							<input
								type="number"
								min={0}
								className={INPUT}
								value={form.est_mtu}
								onChange={(e) => {
									const v = e.target.value;
									setForm((p) => ({ ...p, est_mtu: v }));
								}}
								data-testid="apptrove-pricing-mtu"
							/>
						</label>
					</div>

					<fieldset className="flex flex-col gap-2">
						<legend className={LABEL}>Platforms</legend>
						<div className="flex flex-wrap gap-3">
							{(['ios', 'android', 'web', 'ctv'] as Platform[]).map((p) => (
								<label
									key={p}
									className="inline-flex items-center gap-2 text-sm text-zinc-700"
								>
									<input
										type="checkbox"
										checked={form.platforms[p]}
										onChange={() => handlePlatformToggle(p)}
										className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600"
										data-testid={`apptrove-pricing-platform-${p}`}
									/>
									<span>{p.toUpperCase()}</span>
								</label>
							))}
						</div>
					</fieldset>

					<fieldset className="flex flex-col gap-2">
						<legend className={LABEL}>Add-ons</legend>
						<div className="flex flex-wrap gap-3">
							{(
								[
									['skan', 'SKAN'],
									['ctv', 'CTV'],
									['pltv', 'pLTV'],
									['incrementality', 'Incrementality'],
									['s2s', 'Server-to-server'],
								] as [keyof AddonFlags, string][]
							).map(([k, label]) => (
								<label
									key={k}
									className="inline-flex items-center gap-2 text-sm text-zinc-700"
								>
									<input
										type="checkbox"
										checked={!!form.addons[k]}
										onChange={() => handleAddonToggle(k)}
										className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600"
										data-testid={`apptrove-pricing-addon-${k}`}
									/>
									<span>{label}</span>
								</label>
							))}
						</div>
					</fieldset>

					<div className="grid gap-3 sm:grid-cols-2">
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Contact email</span>
							<input
								type="email"
								className={INPUT}
								value={form.contact_email}
								onChange={(e) => {
									const v = e.target.value;
									setForm((p) => ({ ...p, contact_email: v }));
								}}
								required
								data-testid="apptrove-pricing-email"
							/>
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Notes (optional)</span>
							<input
								type="text"
								className={INPUT}
								value={form.notes}
								onChange={(e) => {
									const v = e.target.value;
									setForm((p) => ({ ...p, notes: v }));
								}}
								data-testid="apptrove-pricing-notes"
							/>
						</label>
					</div>

					<div className="flex items-center gap-3">
						<button
							type="submit"
							className={BTN}
							disabled={createMut.isPending}
							data-testid="apptrove-pricing-submit"
						>
							{createMut.isPending ? 'Sending.' : 'Request quote'}
						</button>
						{createMut.error ? (
							<p className="text-xs text-red-600" role="alert">
								{createMut.error.message}
							</p>
						) : null}
					</div>
				</form>
			</section>

			<section className={CARD} aria-label="Existing quotes">
				<h2 className="text-base font-semibold text-zinc-900">Your quotes</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="apptrove-pricing-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : quotes.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="apptrove-pricing-empty"
					>
						No quotes requested yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="apptrove-pricing-list"
					>
						{quotes.map((q) => (
							<li
								key={q.id}
								className="flex flex-col gap-2 py-3 text-sm"
								data-testid={`apptrove-pricing-row-${q.id}`}
							>
								<div className="flex flex-wrap items-center gap-3">
									<span
										className={`inline-flex items-center rounded-[4px] border px-2 py-0.5 text-xs font-medium ${statusPillClass(q.status)}`}
										data-testid={`apptrove-pricing-status-${q.id}`}
									>
										{q.status}
									</span>
									<span className={CHIP}>{q.scale_band}</span>
									<span className="text-xs text-zinc-500">
										{formatDate(q.created_at)}
									</span>
									<span className="ml-auto font-mono text-xs text-zinc-500">
										{q.id.slice(0, 8)}
									</span>
								</div>
								<div className="flex flex-wrap gap-3 text-xs text-zinc-600">
									<span>
										Installs {q.est_installs_monthly.toLocaleString()}/mo
									</span>
									<span>MAU {q.est_mau.toLocaleString()}</span>
									<span>Platforms: {q.platforms.join(', ') || '-'}</span>
									<span>Contact: {q.contact_email}</span>
								</div>
								<div className="flex flex-wrap items-center gap-3">
									<span className="text-sm font-semibold text-green-800">
										{formatMoney(q.quoted_amount_minor, q.quoted_currency)}
									</span>
									{q.status === 'quoted' ? (
										<>
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() =>
													statusMut.mutate({
														id: q.id,
														status: 'accepted',
													})
												}
												disabled={statusMut.isPending}
												data-testid={`apptrove-pricing-accept-${q.id}`}
											>
												Accept
											</button>
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() =>
													statusMut.mutate({
														id: q.id,
														status: 'declined',
													})
												}
												disabled={statusMut.isPending}
												data-testid={`apptrove-pricing-decline-${q.id}`}
											>
												Decline
											</button>
										</>
									) : null}
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default ApptrovePricingPage;
